import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import type { Node } from "@xyflow/react";

import { bulkUpdateWorkspaceNodePositions } from "../../../services/workspacesApi";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import { toastWorkspaceApiError } from "../../../utils/workspaceApiErrors";
import type { WorkspaceFlowNodeData } from "./workspaceGraphUtils";
import {
  shouldSkipWorkspacePositionPersist,
  workspacePersistNodeId,
} from "./workspaceOptimisticReport";

const DEBOUNCE_MS = 500;

function toPositionPayload(nodes: Array<Node<WorkspaceFlowNodeData>>) {
  return nodes
    .map((node) => {
      const workspaceNode = node.data?.workspaceNode;
      if (workspaceNode && shouldSkipWorkspacePositionPersist(workspaceNode)) {
        return null;
      }
      return {
        nodeId: workspaceNode ? workspacePersistNodeId(workspaceNode) : node.id,
        x: node.position.x,
        y: node.position.y,
      };
    })
    .filter((entry): entry is { nodeId: string; x: number; y: number } => entry != null);
}

export function useWorkspacePositionSave(
  scope: WorkspaceAccountScope | null,
  workspaceId: string | undefined
): (nodes: Array<Node<WorkspaceFlowNodeData>>, options?: { immediate?: boolean }) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Array<{ nodeId: string; x: number; y: number }>>([]);
  const scopeRef = useRef(scope);
  const workspaceIdRef = useRef(workspaceId);

  scopeRef.current = scope;
  workspaceIdRef.current = workspaceId;

  const persistPositions = useCallback(
    async (positions: Array<{ nodeId: string; x: number; y: number }>) => {
      const activeScope = scopeRef.current;
      const activeWorkspaceId = workspaceIdRef.current;
      if (!activeScope || !activeWorkspaceId || positions.length === 0) return;

      try {
        await bulkUpdateWorkspaceNodePositions(activeScope, activeWorkspaceId, { positions });
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to save node positions");
      }
    },
    []
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const pending = pendingRef.current;
      if (pending.length > 0) {
        void persistPositions(pending);
      }
    };
  }, [persistPositions]);

  return useCallback(
    (nodes: Array<Node<WorkspaceFlowNodeData>>, options?: { immediate?: boolean }) => {
      if (!scopeRef.current || !workspaceIdRef.current || nodes.length === 0) return;

      pendingRef.current = toPositionPayload(nodes);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;

      if (options?.immediate) {
        void persistPositions(pendingRef.current);
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const positions = pendingRef.current;
        if (positions.length === 0) return;
        void persistPositions(positions);
      }, DEBOUNCE_MS);
    },
    [persistPositions]
  );
}
